import { PERMISSIONS } from '@navicosa/shared-types';
import { useQueries, useQuery } from '@tanstack/react-query';
import {
  Alert,
  App,
  Col,
  DatePicker,
  Divider,
  Modal,
  Row,
  Select,
  Space,
  Tag,
  Typography,
} from 'antd';
import dayjs, { type Dayjs } from 'dayjs';
import { useMemo, useState } from 'react';
import { api, errorText } from '../../api/client';
import type {
  AvailableEquipment,
  BookingDetail,
  QuotationDetail,
  QuotationItem,
  Supplier,
} from '../../api/types';
import { useAuth } from '../../auth/AuthContext';

/** "eq:<id>" = xe cụ thể, "sup:<id>" = đối tác chưa định danh xe (CR-10) */
type Pick = string | undefined;

/**
 * Chuyển báo giá đã chấp nhận thành đơn thuê. Các dòng tính tiền của cùng thiết bị yêu cầu
 * (ví dụ 1 tuần + 1 ngày) là cùng những chiếc xe → phân bổ theo nhóm.
 */
export function ConvertModal({
  quotation,
  onClose,
  onDone,
}: {
  quotation: QuotationDetail;
  onClose: () => void;
  onDone: (bookingId: string) => void;
}) {
  const { message } = App.useApp();
  const { can } = useAuth();
  const r = quotation.request;
  const [dates, setDates] = useState({
    delivery_at: r.delivery_at ? dayjs(r.delivery_at) : null,
    rental_start_at: dayjs(r.rental_start_at),
    rental_end_at: r.rental_end_at ? dayjs(r.rental_end_at) : null,
    pickup_at: r.pickup_at ? dayjs(r.pickup_at) : null,
  } as Record<string, Dayjs | null>);
  const [picks, setPicks] = useState<Record<string, Pick>>({});
  const [saving, setSaving] = useState(false);

  const groups = useMemo(() => {
    const m = new Map<string, QuotationItem[]>();
    for (const l of quotation.items.filter((i) => i.item_type === 'rental')) {
      const k = l.request_item_id ?? l.id;
      m.set(k, [...(m.get(k) ?? []), l]);
    }
    return [...m.values()].map((lines) => ({ primary: lines[0]!, lines }));
  }, [quotation.items]);

  const from = (dates.delivery_at ?? dates.rental_start_at)!;
  const to = dates.pickup_at ?? dates.rental_end_at;
  const productIds = [...new Set(groups.map((g) => g.primary.product_id!))];
  const avail = useQueries({
    queries: productIds.map((pid) => ({
      queryKey: ['available', pid, from.toISOString(), to?.toISOString()],
      queryFn: () =>
        api.get<AvailableEquipment[]>('/admin/equipment-available', {
          product_id: pid,
          from: from.toISOString(),
          to: to!.toISOString(),
        }),
      enabled: !!to && to.isAfter(from),
    })),
  });
  const suppliers = useQuery({
    queryKey: ['suppliers-pick'],
    queryFn: async () => (await api.page<Supplier>('/admin/suppliers', { limit: 100 })).items,
    enabled: can(PERMISSIONS.SUPPLIERS_READ),
  });

  const chosenEq = new Set(Object.values(picks).filter((v) => v?.startsWith('eq:')));
  const optionsFor = (pid: string, current: Pick) => {
    const eq = avail[productIds.indexOf(pid)]?.data ?? [];
    return [
      {
        label: 'Xe còn trống trong thời gian thuê',
        options: eq.map((e) => ({
          value: `eq:${e.id}`,
          label: `${e.equipment_code}${e.model ? ` · ${e.model}` : ''}${e.ownership_type === 'partner' ? ` (đối tác${e.supplier ? `: ${e.supplier.name}` : ''})` : ''}${e.location ? ` · ${e.location.name}` : ''}`,
          disabled: chosenEq.has(`eq:${e.id}`) && current !== `eq:${e.id}`,
        })),
      },
      ...(suppliers.data?.length
        ? [
            {
              label: 'Đối tác cung cấp – gán xe cụ thể sau',
              options: suppliers.data.map((s) => ({ value: `sup:${s.id}`, label: s.name })),
            },
          ]
        : []),
    ];
  };

  const unitKeys = groups.flatMap((g) =>
    Array.from({ length: g.primary.quantity }, (_, i) => `${g.primary.id}#${i}`),
  );
  const complete = unitKeys.every((k) => picks[k]);

  const submit = async () => {
    setSaving(true);
    try {
      const b = await api.post<BookingDetail>(
        `/admin/quotations/${quotation.id}/convert-to-booking`,
        {
          delivery_at: dates.delivery_at?.toISOString() ?? null,
          rental_start_at: dates.rental_start_at!.toISOString(),
          rental_end_at: dates.rental_end_at?.toISOString() ?? null,
          pickup_at: dates.pickup_at?.toISOString() ?? null,
          units: unitKeys.map((k) => {
            const v = picks[k]!;
            const lineId = k.split('#')[0]!;
            return v.startsWith('eq:')
              ? { quotation_item_id: lineId, equipment_id: v.slice(3) }
              : { quotation_item_id: lineId, supplier_id: v.slice(4) };
          }),
        },
      );
      message.success(`Đã tạo đơn ${b.booking_code}`);
      onDone(b.id);
    } catch (e) {
      message.error(errorText(e));
    } finally {
      setSaving(false);
    }
  };

  const dp = (k: string, label: string, required = false) => (
    <Col xs={12} md={6}>
      <Typography.Text type="secondary">{label}</Typography.Text>
      <DatePicker
        style={{ width: '100%' }}
        format="DD/MM/YYYY"
        allowClear={!required}
        value={dates[k]}
        onChange={(v) => setDates((d) => ({ ...d, [k]: v }))}
      />
    </Col>
  );

  return (
    <Modal
      open
      width={820}
      title={`Chuyển báo giá ${quotation.quote_code} thành đơn thuê`}
      okText="Tạo đơn thuê & giữ lịch xe"
      okButtonProps={{ disabled: !complete, loading: saving }}
      onCancel={onClose}
      onOk={() => void submit()}
    >
      <Row gutter={12}>
        {dp('delivery_at', 'Giao xe')}
        {dp('rental_start_at', 'Bắt đầu tính tiền', true)}
        {dp('rental_end_at', 'Kết thúc tính tiền')}
        {dp('pickup_at', 'Thu hồi xe')}
      </Row>
      {!to && (
        <Alert
          style={{ marginTop: 12 }}
          type="warning"
          showIcon
          message="Chưa có ngày kết thúc/thu hồi: chỉ chọn được đối tác, chưa giữ lịch xe cụ thể."
        />
      )}
      {to && (
        <Typography.Paragraph type="secondary" style={{ marginTop: 8 }}>
          Lịch xe được giữ từ {from.format('DD/MM/YYYY')} đến {to.format('DD/MM/YYYY')}. Danh sách
          chỉ hiện xe sẵn sàng và chưa có lịch trùng.
        </Typography.Paragraph>
      )}
      <Divider style={{ margin: '12px 0' }} />
      {groups.map((g) => (
        <div key={g.primary.id} style={{ marginBottom: 16 }}>
          <Space wrap>
            <Typography.Text strong>{g.primary.product_name_snapshot}</Typography.Text>
            <Tag>{g.primary.quantity} xe</Tag>
            {g.lines.length > 1 && (
              <Typography.Text type="secondary">
                tính tiền{' '}
                {g.lines
                  .map(
                    (l) =>
                      `${Number(l.period_count)} ${l.rental_period === 'week' ? 'tuần' : l.rental_period === 'month' ? 'tháng' : 'ngày'}`,
                  )
                  .join(' + ')}
              </Typography.Text>
            )}
          </Space>
          {Array.from({ length: g.primary.quantity }, (_, i) => {
            const k = `${g.primary.id}#${i}`;
            return (
              <Row key={k} gutter={8} align="middle" style={{ marginTop: 6 }}>
                <Col flex="64px">Xe {i + 1}</Col>
                <Col flex="auto">
                  <Select
                    style={{ width: '100%' }}
                    showSearch
                    optionFilterProp="label"
                    placeholder="Chọn xe hoặc đối tác"
                    value={picks[k]}
                    loading={avail[productIds.indexOf(g.primary.product_id!)]?.isLoading}
                    options={optionsFor(g.primary.product_id!, picks[k])}
                    onChange={(v) => setPicks((p) => ({ ...p, [k]: v }))}
                    notFoundContent="Không có xe trống – chọn đối tác hoặc đổi ngày"
                  />
                </Col>
              </Row>
            );
          })}
        </div>
      ))}
    </Modal>
  );
}
